import { Analytics } from '@cobliteam/analytics';
import type { LocalUserPreferences } from '@cobliteam/api-lib';
import { useActivationTasks } from '@cobliteam/hooks';
import type { Permission } from '@cobliteam/permissions';
import { usePermissions } from '@cobliteam/permissions';
import { Button, Popover, Tooltip } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivationTasksTypes } from '../ActivationTasksCommon';
import ActivationTasksSkeleton from '../ActivationTasksSkeleton';
import OnboardingActivationTasks from './OnboardingActivationTasks';
import { ActivationTasksList, getOnboardingTaskConfiguration } from './OnboardingActivationTasksTypes';

const OnboardingActivationTasksPopover = () => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const { useGetUserActivationTasks } = useActivationTasks();
  const { data: userActivationTasks, isLoading, refetch } = useGetUserActivationTasks();

  const onboardingActivationTasks = useMemo(() => {
    return getOnboardingTaskConfiguration();
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');

  const initialCheckedActivationTasks = useMemo(() => {
    return userActivationTasks?.filter((task) => task.finished).map((task) => task.name) as ActivationTasksTypes[];
  }, [userActivationTasks]);

  const hideOnboardingBadge = useMemo(() => !preferences?.hideOnboardingBadge, [preferences?.hideOnboardingBadge]);

  const totalActivationTasks = useMemo(() => {
    let count = 0;
    for (const category of ActivationTasksList) {
      for (const task of category.steps) {
        if (isAccessAllowed(task.permissions as Permission[])) {
          count++;
        }
      }
    }
    return count;
  }, [isAccessAllowed]);

  const handleOpenActivationTasks = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        refetch();
      } else {
        setPreferences((prevPrefs) => ({
          ...prevPrefs,
          hideOnboardingBadge: true,
        }));
      }

      return !prev;
    });

    Analytics.track('Activation Tasks - Sidebar button clicked', { type: 'onboarding' });
  }, [refetch, setPreferences]);

  if (totalActivationTasks === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenActivationTasks}>
      <Tooltip content={t('onboarding_activation_tasks.tooltip')}>
        <PopoverTrigger asChild>
          <div className='relative mt-1.5'>
            <Button
              data-testid='app-header-activation-tasks-button'
              className='text-default hover:text-purple-12'
              variant='surface'
              color='purple'
              size='sm'
              icon={<Icon name='book-marked' color='purple' />}
              aria-label='Ver activation tasks'
            />
            {hideOnboardingBadge && (
              <span className='-top-0.5 -right-0.5 absolute flex size-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-accessory-purple-11 opacity-75' />
                <span className='relative inline-flex size-2 rounded-full bg-accessory-purple-11' />
              </span>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='!px-0 flex max-h-[80vh] w-auto min-w-[32rem] flex-col overflow-hidden border-accessory-purple-8'>
        {isLoading ? (
          <ActivationTasksSkeleton />
        ) : (
          <OnboardingActivationTasks initialCheckedActivationTasks={initialCheckedActivationTasks} onFinishOnboarding={() => setIsOpen(false)} />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default OnboardingActivationTasksPopover;
